import { useState } from 'react';
import { Passenger } from '../types';

interface PassengerSelectorProps {
  count: number;
  onChange: (count: number) => void;
  passengers: Passenger[];
  onPassengerUpdate: (index: number, data: Partial<Passenger>) => void;
}

export function PassengerSelector({ count, onChange, passengers, onPassengerUpdate }: PassengerSelectorProps) {
  const [showDetails, setShowDetails] = useState(false);

  const defaultPassenger: Passenger = {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    passportNumber: '',
    specialNeeds: '',
  };

  return (
    <div className="w-full">
      <label className="block text-xs font-mono text-orbital-cyan uppercase tracking-widest mb-3">
        Number of Passengers [CREW_COUNT]
      </label>

      {/* Passenger count selector */}
      <div className="flex gap-2 items-center">
        <button
          type="button"
          onClick={() => onChange(Math.max(1, count - 1))}
          className="w-12 h-12 bg-orbital-card border border-orbital-dim hover:border-orbital-cyan text-orbital-cyan font-mono text-xl transition-colors"
        >
          -
        </button>

        <div className="flex-1 text-center">
          <span className="text-2xl font-mono text-orbital-primary">{count}</span>
          <div className="text-xs text-gray-500 font-mono">CREW MEMBER{count !== 1 ? 'S' : ''}</div>
        </div>

        <button
          type="button"
          onClick={() => onChange(Math.min(8, count + 1))}
          className="w-12 h-12 bg-orbital-card border border-orbital-dim hover:border-orbital-cyan text-orbital-cyan font-mono text-xl transition-colors"
        >
          +
        </button>
      </div>

      {/* Expand for passenger details */}
      <button
        type="button"
        onClick={() => setShowDetails(!showDetails)}
        className="mt-3 text-xs text-orbital-magenta hover:text-orbital-magenta/80 font-mono flex items-center gap-2"
      >
        <span>{showDetails ? '▼' : '▶'}</span>
        {showDetails ? 'HIDE' : 'ENTER'} PASSENGER DETAILS
      </button>

      {/* Passenger forms */}
      {showDetails && (
        <div className="mt-4 space-y-4 border-t border-orbital-dim pt-4">
          {Array.from({ length: count }).map((_, idx) => {
            const passenger = passengers[idx] || { ...defaultPassenger };
            return (
              <div
                key={idx}
                className="bg-orbital-card/50 border border-orbital-dim p-4"
              >
                <div className="text-xs text-orbital-magenta font-mono mb-3">
                  CREW MEMBER #{idx + 1}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">First Name</label>
                    <input
                      type="text"
                      value={passenger.firstName}
                      onChange={(e) => onPassengerUpdate(idx, { firstName: e.target.value })}
                      className="w-full bg-orbital-bg border border-orbital-dim text-orbital-primary px-3 py-2 font-mono text-sm focus:border-orbital-cyan focus:outline-none"
                      placeholder="ENTER NAME"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={passenger.lastName}
                      onChange={(e) => onPassengerUpdate(idx, { lastName: e.target.value })}
                      className="w-full bg-orbital-bg border border-orbital-dim text-orbital-primary px-3 py-2 font-mono text-sm focus:border-orbital-cyan focus:outline-none"
                      placeholder="ENTER SURNAME"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Date of Birth</label>
                    <input
                      type="date"
                      value={passenger.dateOfBirth}
                      onChange={(e) => onPassengerUpdate(idx, { dateOfBirth: e.target.value })}
                      className="w-full bg-orbital-bg border border-orbital-dim text-orbital-primary px-3 py-2 font-mono text-sm focus:border-orbital-cyan focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Passport Number</label>
                    <input
                      type="text"
                      value={passenger.passportNumber}
                      onChange={(e) => onPassengerUpdate(idx, { passportNumber: e.target.value })}
                      className="w-full bg-orbital-bg border border-orbital-dim text-orbital-primary px-3 py-2 font-mono text-sm focus:border-orbital-cyan focus:outline-none uppercase"
                      placeholder="PASSPORT ID"
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <label className="block text-xs text-gray-500 mb-1">Special Needs / Medical Notes</label>
                  <textarea
                    value={passenger.specialNeeds}
                    onChange={(e) => onPassengerUpdate(idx, { specialNeeds: e.target.value })}
                    rows={2}
                    className="w-full bg-orbital-bg border border-orbital-dim text-orbital-primary px-3 py-2 font-mono text-sm focus:border-orbital-cyan focus:outline-none resize-none"
                    placeholder="ALLERGIES, MEDICAL CONDITIONS, ACCESSIBILITY REQUIREMENTS..."
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
